import { createAction, createSlice } from '@reduxjs/toolkit';
import { logout } from './authSlice';
import { normOption } from '../lib/agent/options';
import { knownWatchType } from '../lib/security';

/**
 * What the agent may fill in and ask for, in the Ki_UI vocabulary.
 *
 * The agent does not call APIs through the client. It fills a form here with an
 * `UPDATE_*` action and then fires a `REQUEST_*` signal; the screen that is
 * open runs the same code its own button runs, reading its inputs from these
 * forms. The action types are the literal strings the agent dispatches
 * (`UPDATE_CLIENT_FORM`, not `agent/updateClientForm`), because they are also
 * the names written into `src/config/pms_ui_discovery_summary.json` — the
 * catalogue the backend loads into its prompts. `scripts/check-ui-discovery.mjs`
 * fails when that file and `AGENT_FORMS` / `AGENT_SIGNALS` disagree.
 *
 * Everything the agent sends is untrusted model output, so each form keeps only
 * the fields it declares and holds each to its type: text is trimmed, numbers
 * must be finite, booleans are real booleans, and an enum value outside its
 * list is dropped rather than stored for a mutation to reject later. `null`
 * (or blank text) clears a field; a field left out (`undefined`) is untouched,
 * so the agent can fill a form one field per turn.
 *
 * Not persisted: a half-filled form belongs to one conversation, and is cleared
 * on sign-out so the next account never inherits it.
 */

/* ── field types ─────────────────────────────────────────────────────────── */

/**
 * A sanitiser turns what the agent sent into what the form stores: a value,
 * `null` to clear the field, or `undefined` to drop an unusable value and
 * keep whatever the field held before.
 */

const isPlainObject = (v) => v != null && typeof v === 'object' && !Array.isArray(v);

/** Trimmed text. A finite number is accepted as text: a phone number often arrives as one. */
const text = (raw) => {
  if (typeof raw === 'number') return Number.isFinite(raw) ? String(raw) : undefined;
  if (typeof raw !== 'string') return undefined;
  const s = raw.trim();
  return s || null;
};

/** A finite number, from a number or numeric text. */
const number = (raw) => {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : undefined;
  if (typeof raw !== 'string') return undefined;
  const s = raw.trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
};

/** A boolean, from `true`/`false` or the same words as text. "yes" is not guessed at. */
const boolean = (raw) => {
  if (typeof raw === 'boolean') return raw;
  if (typeof raw !== 'string') return undefined;
  const s = raw.trim().toLowerCase();
  if (!s) return null;
  if (s === 'true') return true;
  if (s === 'false') return false;
  return undefined;
};

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** A real calendar day as YYYY-MM-DD; 2026-02-30 is dropped, not stored. */
const date = (raw) => {
  const s = text(raw);
  if (s == null) return s;
  if (!ISO_DAY.test(s)) return undefined;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s ? s : undefined;
};

/**
 * One of `values`, stored in its canonical spelling. Matched ignoring case,
 * spaces, underscores and hyphens — the same rule the agent's tabs and screen
 * names follow — so "external" stores `EXTERNAL` and "mutual fund" stores
 * `mutual_fund`, and anything else is dropped.
 */
const oneOf = (...values) => (raw) => {
  const s = text(raw);
  if (s == null) return s;
  const want = normOption(s);
  return values.find((v) => normOption(v) === want);
};

/**
 * A `securityType` the watchlist register actually has, or dropped.
 *
 * `oneOf` is not enough here. The register says `mf` (`SecurityTypeEnum`, which
 * the watchlist mutations validate against before their resolver runs) while
 * every prompt, worked example and catalogue line says `mutual_fund` — which
 * `oneOf` normalises to `mutualfund`, matches against nothing, and drops. The
 * write then went out under the default and the agent could never name a
 * scheme at all. `knownWatchType` is the same vocabulary the writes use, so
 * the two cannot drift.
 */
const watchSecurityType = (raw) => {
  const s = text(raw);
  return s == null ? s : knownWatchType(s);
};

/**
 * A list of whole numbers, from an array or text such as "0, 2, 5". One item
 * that is not a whole number (2.5, "two") drops the list: the backtest takes
 * `[Int!]`, and a list quietly missing an item would sweep something else.
 */
const wholeNumberList = (raw) => {
  let items;
  if (Array.isArray(raw)) items = raw;
  else if (typeof raw === 'string') items = raw.split(/[\s,]+/).filter(Boolean);
  else return undefined;
  if (!items.length) return null;
  const out = items.map((item) => number(item));
  return out.every((n) => Number.isInteger(n)) ? out : undefined;
};

/** Trimmed text in upper case, the way the KYC record stores gender and marital status (MALE, MARRIED). */
const upperText = (raw) => {
  const s = text(raw);
  return s == null ? s : s.toUpperCase();
};

/** A whole number of zero or more, from a number or numeric text; 2.5 and -1 are dropped. */
const wholeCount = (raw) => {
  const n = number(raw);
  if (n == null) return n;
  return Number.isInteger(n) && n >= 0 ? n : undefined;
};

const withoutSpaces = (s) => s.replace(/\s+/g, '');

/**
 * Text that must match `pattern` once `prepare` has normalised it (spaces
 * removed, upper-cased), stored in that normalised form. For identifiers whose
 * shape is fixed — a mobile number, a PIN code, PAN, Aadhaar, a bank account,
 * IFSC — a value of the wrong shape is a mistake, and storing it would only
 * move the failure to the save. A number is accepted only while it is a safe
 * integer: past that, JavaScript has already changed its digits.
 */
const matching = (pattern, prepare = (s) => s) => (raw) => {
  if (typeof raw === 'number' && !Number.isSafeInteger(raw)) return undefined;
  const s = text(raw);
  if (s == null) return s;
  const value = prepare(s);
  return pattern.test(value) ? value : undefined;
};

/* The shapes the KYC sections enforce. A mobile number is the ten digits
   without the country code, which is all the section's Mobile input takes. A
   bare 91 is read as the country code only in front of ten more digits, so a
   number that itself starts with 91 is kept. */
const mobileNumber = matching(/^\d{10}$/, (s) => withoutSpaces(s).replace(/^\+91|^91(?=\d{10}$)/, ''));
const pincode = matching(/^\d{6}$/);
const panNumber = matching(/^[A-Z]{5}[0-9]{4}[A-Z]$/, (s) => s.toUpperCase());
const aadharNumber = matching(/^\d{12}$/, withoutSpaces);
const accountNumber = matching(/^\d{6,20}$/, withoutSpaces);
const ifscCode = matching(/^[A-Z]{4}0[A-Z0-9]{6}$/, (s) => s.toUpperCase());

/**
 * The client a KYC update was sent for, read from where the executor says it
 * landed (`meta.screen`, `meta.params`): the route id on KycClient, the
 * `client` param on Kyc. Anywhere else, and for an update dispatched without
 * meta, it names no client and the answer is null.
 */
const kycClientOf = (meta) => {
  const params = isPlainObject(meta?.params) ? meta.params : {};
  let id = null;
  if (meta?.screen === 'kycClient') id = params.id;
  else if (meta?.screen === 'kyc') id = params.client;
  return text(id) ?? null;
};

const HOLDING_FIELDS = {
  securitySymbol: text,
  quantity: number,
  price: number,
  addedDate: date,
};

/**
 * Holdings rows `{ securitySymbol, quantity, price, addedDate }`. Each row keeps
 * only those fields, each held to its type; a row with no symbol names nothing
 * and is dropped. A value that is not a list is dropped whole.
 */
const holdings = (raw) => {
  if (!Array.isArray(raw)) return undefined;
  const rows = [];
  raw.forEach((item) => {
    if (!isPlainObject(item)) return;
    const row = {};
    Object.entries(HOLDING_FIELDS).forEach(([field, sanitise]) => {
      const value = item[field] === undefined ? undefined : sanitise(item[field]);
      if (value != null) row[field] = value;
    });
    if (row.securitySymbol) rows.push(row);
  });
  return rows.length ? rows : null;
};

/* The analysis actions a proposal runs for: the ids of PROPOSAL_ACTIONS
   (src/api/hooks/proposals.js), listed here so the slice imports no Apollo
   hooks. tests/agent/proposals.test.js holds the two lists equal. */
const PROPOSAL_ACTION_TYPES = [
  'rebalance_portfolio', 'concentration_fix', 'risk_reduction', 'tax_efficiency', 'cash_invest', 'raise_cash',
  'exit_position', 'swap_holding', 'exposure_replacement', 'stp_swap', 'swp_setup', 'analyse_security',
];

/**
 * The portfolio a proposal update was sent for: Proposals' `portfolio` param
 * where the executor says it landed. Anywhere else, and for an update
 * dispatched without meta, it names no portfolio and the answer is null.
 */
const proposalPortfolioOf = (meta) => {
  const params = isPlainObject(meta?.params) ? meta.params : {};
  return meta?.screen === 'proposals' ? text(params.portfolio) ?? null : null;
};

const TRADE_FIELDS = {
  securitySymbol: text,
  action: text,
  amount: number,
};

/**
 * Proposal table lines `{ securitySymbol, action, amount }`, each keeping only
 * those fields held to their types. A line with no symbol names nothing and is
 * dropped; a later line for the same symbol replaces an earlier one. `action`
 * stays text: Proposals resolves it against its own Action options ('Buy' is
 * ADD) and names one that matches none of them when it runs, rather than the
 * slice dropping it unseen. A value that is not a list is dropped whole; an
 * empty result is null, which clears the lines.
 */
const trades = (raw) => {
  if (!Array.isArray(raw)) return undefined;
  const bySymbol = new Map();
  raw.forEach((item) => {
    if (!isPlainObject(item)) return;
    const line = {};
    Object.entries(TRADE_FIELDS).forEach(([field, sanitise]) => {
      const value = item[field] === undefined ? undefined : sanitise(item[field]);
      if (value != null) line[field] = value;
    });
    if (!line.securitySymbol) return;
    bySymbol.delete(line.securitySymbol);
    bySymbol.set(line.securitySymbol, line);
  });
  return bySymbol.size ? [...bySymbol.values()] : null;
};

/* ── the forms ───────────────────────────────────────────────────────────── */

/**
 * Every form: the action that fills it, the state key it lives under, and its
 * fields with their types. `AGENT_FORMS` below is derived from this, so the
 * field list the check script compares against the catalogue is the same list
 * the reducer cherry-picks.
 *
 * A form that belongs to one subject also names `owner`: the state key that
 * records whose values it holds, and how to read that subject from an
 * update's `meta`.
 */
const FORM_SPECS = {
  /**
   * `clientForm` — the Dashboard's "Add client / prospect" dialog.
   * Submitted by `REQUEST_CLIENT_SUBMIT` on the Dashboard (addFirmClient — a
   * consent request or invite — or createProspect, by `kind`).
   */
  UPDATE_CLIENT_FORM: {
    form: 'clientForm',
    fields: {
      kind: oneOf('client', 'prospect'),
      name: text,
      email: text,
      phoneNumber: text,
      company: text,
      notes: text,
    },
  },
  /**
   * `portfolioForm` — the Dashboard's "New portfolio" dialog, for a client.
   * Submitted by `REQUEST_PORTFOLIO_SUBMIT` on the Dashboard: an empty
   * portfolio, or one built from `holdings` when `mode` is 'holdings'.
   */
  UPDATE_PORTFOLIO_FORM: {
    form: 'portfolioForm',
    fields: {
      clientId: text,
      name: text,
      portfolioType: oneOf('EXTERNAL', 'ACCOUNT', 'MF'),
      mode: oneOf('empty', 'holdings'),
      holdings,
    },
  },
  /**
   * `alphaForm` — the alpha model's run settings: the backtest form on the Runs
   * tab and the as-of scorecard on the As-of tab. Read by
   * `REQUEST_RUN_ANALYSIS` on the alpha screen, which runs whatever its `tab`
   * param names.
   */
  UPDATE_ALPHA_DATA: {
    form: 'alphaForm',
    fields: {
      horizon: text,
      windowFrom: date,
      windowTo: date,
      topFraction: number,
      includeReturns: boolean,
      includePicks: boolean,
      decayLags: wholeNumberList,
      notes: text,
      asOfDate: date,
    },
  },
  /**
   * `stockForm` — research feedback on a stock's analysis report, and the
   * verified flag. `REQUEST_STOCK_FEEDBACK_SUBMIT` sends `feedbackText`;
   * `REQUEST_STOCK_VERIFY_SUBMIT` sets `verified`. Both on the stock screen.
   */
  UPDATE_STOCK_DATA: {
    form: 'stockForm',
    fields: {
      feedbackText: text,
      verified: boolean,
    },
  },
  /**
   * `kycForm` — one client's KYC record, the fields in the order KycClient's
   * sections show them: Personal details, Address, Financial profile,
   * Identity, Bank, Declarations, Consents. One form rather than one per
   * section: the page knows which fields make up a section, and
   * `REQUEST_KYC_SECTIONS_SUBMIT` on KycClient saves each section that holds a
   * value, whole and on its own, because the backend works out the onboarding
   * step from the fields a save carries.
   *
   * `email` is also where a fresh eKYC invitation goes: `REQUEST_EKYC_SUBMIT`
   * reads it on Kyc (for the client in its `client` param) and on KycClient,
   * where it is the personal-details email as well.
   *
   * The record is one client's, so the form remembers which (`kycFormClient`).
   * An update sent on KycClient or on Kyc with a client names that client, and
   * when it is not the client the form holds values for, those values are
   * dropped first: a PAN typed for one client must never show on, or be saved
   * into, another's record. An update that names no client (sent on another
   * screen, or dispatched without meta) merges as before and leaves the owner
   * as it was. The pages act on the form only for its owner
   * (`selectKycFormFor`).
   *
   * Identifiers are held to their shape here, since a PAN or IFSC of the wrong
   * shape can only fail the save later. The enum-like fields (state, country,
   * communication mode, income, experience, risk profile, account type,
   * dividend mode) stay trimmed text rather than `oneOf`: their option lists
   * come from the backend when the page loads, so the page resolves them at
   * save time and names the options when one does not match.
   */
  UPDATE_KYC_DATA: {
    form: 'kycForm',
    owner: { key: 'kycFormClient', of: kycClientOf },
    fields: {
      fullName: text,
      email: text,
      mobileNumber,
      dateOfBirth: date,
      placeOfBirth: text,
      gender: upperText,
      maritalStatus: upperText,
      fatherName: text,
      motherName: text,
      address: text,
      city: text,
      state: text,
      pincode,
      country: text,
      communicationMode: text,
      annualIncome: text,
      occupation: text,
      tradingExperience: text,
      riskProfile: text,
      panNumber,
      aadharNumber,
      accountNumber,
      ifscCode,
      branchName: text,
      accountType: text,
      dividendMode: text,
      paysTaxesInIndia: boolean,
      notPoliticallyExposed: boolean,
      acceptIbtTerms: boolean,
      noSebiActions: boolean,
      fundSettlementDays: wholeCount,
      acceptAllTerms: boolean,
      bseStarMfFacility: boolean,
    },
  },
  /**
   * `watchlistForm` — one security to add to or remove from a watchlist.
   * Submitted by `REQUEST_WATCHLIST_SUBMIT`, which the shell handles on every
   * screen (`components/copilot/AgentGlobalActions.js`); `op` defaults to add,
   * `listName` to the default list ("General") — a named list is created by
   * the first name saved to it.
   */
  UPDATE_WATCHLIST_DATA: {
    form: 'watchlistForm',
    fields: {
      securitySymbol: text,
      securityType: watchSecurityType,
      op: oneOf('add', 'remove'),
      listName: text,
    },
  },
  /**
   * `screenSaveForm` — the name a shown screen is kept under.
   *
   * Submitted by `REQUEST_SCREEN_SAVE_SUBMIT` on FundResearch, which supplies
   * the handle from its own URL when the agent does not send one — it is the
   * screen in view, and the agent has no better source for it than the id it
   * handed over in the first place.
   */
  UPDATE_SCREEN_SAVE_DATA: {
    form: 'screenSaveForm',
    fields: {
      name: text,
      urlId: text,
    },
  },
  /**
   * `proposalForm` — Proposals' portfolio tab: the table's lines (`trades`),
   * the notes, the goal, the analysis action and the title a save uses. Laid
   * over the table as it arrives, and read by `REQUEST_RUN_ANALYSIS` and
   * `REQUEST_PROPOSAL_SUBMIT` on Proposals.
   *
   * The lines are one portfolio's, so the form remembers which
   * (`proposalFormPortfolio`, from the `portfolio` param): an update for
   * another portfolio starts from empty, and the screen uses the values only
   * for the portfolio they were set for (`selectProposalFormFor`).
   */
  UPDATE_PROPOSAL_DATA: {
    form: 'proposalForm',
    owner: { key: 'proposalFormPortfolio', of: proposalPortfolioOf },
    fields: {
      trades,
      notes: text,
      goalId: wholeCount,
      actionType: oneOf(...PROPOSAL_ACTION_TYPES),
      title: text,
    },
  },
};

/** `{ [UPDATE_* type]: { form, fields: string[] } }` — the canonical table, frozen. */
export const AGENT_FORMS = Object.freeze(Object.fromEntries(
  Object.entries(FORM_SPECS).map(([type, spec]) => [
    type,
    Object.freeze({ form: spec.form, fields: Object.freeze(Object.keys(spec.fields)) }),
  ]),
));

/**
 * The signals the agent fires once a form is filled (or with none, for a run).
 * Only the screen that is open handles a signal, and only if its catalogue
 * entry declares it; `REQUEST_WATCHLIST_SUBMIT` is handled on any screen.
 */
export const AGENT_SIGNALS = Object.freeze([
  'REQUEST_RUN_ANALYSIS',
  'REQUEST_CLIENT_SUBMIT',
  'REQUEST_PORTFOLIO_SUBMIT',
  'REQUEST_STOCK_FEEDBACK_SUBMIT',
  'REQUEST_STOCK_VERIFY_SUBMIT',
  'REQUEST_EKYC_SUBMIT',
  'REQUEST_KYC_SECTIONS_SUBMIT',
  'REQUEST_KYC_FINAL_SUBMIT',
  'REQUEST_WATCHLIST_SUBMIT',
  'REQUEST_SCREEN_SAVE_SUBMIT',
  'REQUEST_PROPOSAL_SUBMIT',
]);

/* ── action creators ─────────────────────────────────────────────────────── */

/* Form updates carry the fields as their payload: `updateClientForm({ name })`. */
export const updateClientForm = createAction('UPDATE_CLIENT_FORM');
export const updatePortfolioForm = createAction('UPDATE_PORTFOLIO_FORM');
export const updateAlphaData = createAction('UPDATE_ALPHA_DATA');
export const updateStockData = createAction('UPDATE_STOCK_DATA');
export const updateKycData = createAction('UPDATE_KYC_DATA');
export const updateWatchlistData = createAction('UPDATE_WATCHLIST_DATA');
export const updateScreenSaveData = createAction('UPDATE_SCREEN_SAVE_DATA');
export const updateProposalData = createAction('UPDATE_PROPOSAL_DATA');

/**
 * Signals carry no payload. The time a signal was fired rides in `meta.at`,
 * given by the caller: the reducer must stay pure, so it never reads the clock.
 * `requestRunAnalysis({ at: Date.now() })` or `requestRunAnalysis()`.
 */
const signal = (type) => createAction(type, (options) => ({
  payload: undefined,
  meta: { at: Number.isFinite(options?.at) ? options.at : null },
}));

export const requestRunAnalysis = signal('REQUEST_RUN_ANALYSIS');
export const requestClientSubmit = signal('REQUEST_CLIENT_SUBMIT');
export const requestPortfolioSubmit = signal('REQUEST_PORTFOLIO_SUBMIT');
export const requestStockFeedbackSubmit = signal('REQUEST_STOCK_FEEDBACK_SUBMIT');
export const requestStockVerifySubmit = signal('REQUEST_STOCK_VERIFY_SUBMIT');
export const requestEkycSubmit = signal('REQUEST_EKYC_SUBMIT');
export const requestKycSectionsSubmit = signal('REQUEST_KYC_SECTIONS_SUBMIT');
export const requestKycFinalSubmit = signal('REQUEST_KYC_FINAL_SUBMIT');
export const requestWatchlistSubmit = signal('REQUEST_WATCHLIST_SUBMIT');
export const requestScreenSaveSubmit = signal('REQUEST_SCREEN_SAVE_SUBMIT');
export const requestProposalSubmit = signal('REQUEST_PROPOSAL_SUBMIT');

/* ── reducer ─────────────────────────────────────────────────────────────── */

const initialState = {
  clientForm: {},
  portfolioForm: {},
  alphaForm: {},
  stockForm: {},
  kycForm: {},
  /** The client id `kycForm`'s values were set for, as text; null when no update named one. */
  kycFormClient: null,
  watchlistForm: {},
  screenSaveForm: {},
  proposalForm: {},
  /** The portfolio symbol `proposalForm`'s values were set for; null when no update named one. */
  proposalFormPortfolio: null,
  /** The last signal fired: `{ type, at }`, `at` null when the caller gave no time. */
  lastSignal: null,
  /**
   * The data block of the agent's last answer that a screen can draw:
   * `{ suggestedStocks, stockTrades, analysisUrlId, source, reply, at }`.
   * Ideas › Ranked ideas shows `suggestedStocks` as the assistant's picks;
   * Trading accounts, Trade › New trade and Trade portfolio show
   * `stockTrades` without re-running the generator. Null until an answer
   * carries one; replaced by the next answer that does.
   */
  lastPayload: null,
};

/**
 * Merge one update into its form: declared fields only, each sanitised.
 * A payload that is not an object carries no fields and changes nothing.
 */
function applyUpdate(form, fields, payload) {
  if (!isPlainObject(payload)) return;
  Object.entries(fields).forEach(([field, sanitise]) => {
    const raw = payload[field];
    if (raw === undefined) return;
    const value = raw === null ? null : sanitise(raw);
    if (value === undefined) return;
    if (value === null) delete form[field];
    else form[field] = value;
  });
}

const agentSlice = createSlice({
  name: 'agent',
  initialState,
  reducers: {
    /** The agent's last data payload (see `lastPayload`); a null payload clears it. */
    setAgentPayload(state, { payload }) {
      state.lastPayload = isPlainObject(payload) ? payload : null;
    },
    /**
     * Empty one form, by its state key (`'clientForm'`). Pages call it after a
     * successful submit, so the next request starts from what the agent sends
     * rather than from the last client's details. A form with an owner forgets
     * it too: an empty form belongs to nobody. An unknown key does nothing.
     */
    resetAgentForm(state, { payload }) {
      const spec = Object.values(FORM_SPECS).find((s) => s.form === payload);
      if (!spec) return;
      state[spec.form] = {};
      if (spec.owner) state[spec.owner.key] = null;
    },
    /**
     * Remove some fields from one form: `{ form: 'kycForm', fields: ['city'] }`.
     * For a form that several writes share — KycClient saves `kycForm` one
     * section at a time, and sends its email as the eKYC address — so a write
     * clears only what it used and the agent's other values stay. Only the
     * form's declared fields are touched; an unknown form, or `fields` that is
     * not a list, does nothing. The owner is kept, even when the form ends up
     * empty: the values still to come in this conversation are for the same
     * client.
     */
    clearAgentFormFields(state, { payload }) {
      const spec = Object.values(AGENT_FORMS).find((d) => d.form === payload?.form);
      if (!spec || !Array.isArray(payload.fields)) return;
      payload.fields.forEach((field) => {
        if (spec.fields.includes(field)) delete state[spec.form][field];
      });
    },
  },
  extraReducers: (builder) => {
    Object.entries(FORM_SPECS).forEach(([type, spec]) => {
      builder.addCase(type, (state, action) => {
        /* An update for another subject starts that subject's form from empty
           rather than merging into the last one's values. A payload that is not
           an object is no update at all, so it rebinds nothing either. */
        const owner = spec.owner && isPlainObject(action.payload) ? spec.owner.of(action.meta) : null;
        if (owner != null && owner !== state[spec.owner.key]) {
          state[spec.form] = {};
          state[spec.owner.key] = owner;
        }
        applyUpdate(state[spec.form], spec.fields, action.payload);
      });
    });
    AGENT_SIGNALS.forEach((type) => {
      builder.addCase(type, (state, action) => {
        const at = action.meta?.at;
        state.lastSignal = { type, at: Number.isFinite(at) ? at : null };
      });
    });
    builder.addCase(logout, () => initialState);
  },
});

export const { resetAgentForm, clearAgentFormFields, setAgentPayload } = agentSlice.actions;

/* ── selectors ───────────────────────────────────────────────────────────── */

/** Stands in for a form when the slice is absent (a test store), with a stable identity. */
const NO_FORM = Object.freeze({});

export const selectAgentPayload = (state) => state.agent?.lastPayload ?? null;
export const selectClientForm = (state) => state.agent?.clientForm ?? NO_FORM;
export const selectPortfolioForm = (state) => state.agent?.portfolioForm ?? NO_FORM;
export const selectAlphaForm = (state) => state.agent?.alphaForm ?? NO_FORM;
export const selectStockForm = (state) => state.agent?.stockForm ?? NO_FORM;
export const selectKycForm = (state) => state.agent?.kycForm ?? NO_FORM;
export const selectKycFormClient = (state) => state.agent?.kycFormClient ?? null;

/**
 * An owned form when its values may be used for `subject`, else null.
 *
 * They may when they were set for that subject (a client's id, a portfolio's
 * symbol). Values with no owner were set on a screen that names no subject, or
 * dispatched by hand, and nothing says they are this subject's, so they are
 * used for no one. An empty form holds nothing to misuse and is given to
 * anyone, whoever it last belonged to: a page then answers "nothing to save"
 * rather than blaming another subject for values that are no longer there. A page that gets null shows none of the
 * agent's values and saves none of them.
 */
const usableFor = (form, owner, subject) => (
  Object.keys(form).length === 0 || (owner != null && subject != null && owner === String(subject)) ? form : null
);

/** `kycForm` when its values may be used for `clientId` (see `usableFor`), else null. */
export const selectKycFormFor = (state, clientId) => usableFor(
  selectKycForm(state), selectKycFormClient(state), clientId,
);

export const selectWatchlistForm = (state) => state.agent?.watchlistForm ?? NO_FORM;
export const selectScreenSaveForm = (state) => state.agent?.screenSaveForm ?? NO_FORM;
export const selectProposalForm = (state) => state.agent?.proposalForm ?? NO_FORM;
export const selectProposalFormPortfolio = (state) => state.agent?.proposalFormPortfolio ?? null;

/** `proposalForm` when its values may be used for `portfolio` (see `usableFor`), else null. */
export const selectProposalFormFor = (state, portfolio) => usableFor(
  selectProposalForm(state), selectProposalFormPortfolio(state), portfolio,
);

export const selectLastAgentSignal = (state) => state.agent?.lastSignal ?? null;

export default agentSlice.reducer;
